import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV } from "@/constants/testIds";

export const LOGO_SRC = "/brand/levelleven-logo.png";

const MAIN = [
  { to: "/methode", label: "Aanpak" },
  { to: "/programma", label: "START-programma" },
  { to: "/gemeenten", label: "Voor gemeenten" },
  { to: "/verhalen", label: "Deelnemersverhalen" },
  { to: "/over-ons", label: "Over ons" },
  { to: "/contact", label: "Contact" },
];

const MORE = [
  { to: "/voor-jou", label: "Voor deelnemers" },
  { to: "/onderzoek-en-onderbouwing", label: "Onderzoek & onderbouwing" },
  { to: "/programmabouwer", label: "Programmabouwer" },
  { to: "/onderwijs", label: "Voor onderwijs" },
  { to: "/kennisbank", label: "Kennisbank" },
  { to: "/faq", label: "Veelgestelde vragen" },
];

export function Logo({ className = "h-9 sm:h-11" }) {
  return <img src={LOGO_SRC} alt="LevelLeven — in beweging, in ontwikkeling" width="355" height="58" className={`w-auto ${className}`} />;
}

const linkCls = ({ isActive }) =>
  `relative text-[15px] font-medium transition-colors ${isActive ? "text-ll-forest" : "text-ll-ink/80 hover:text-ll-forest"} after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:rounded-full after:bg-ll-orange after:transition-all ${isActive ? "after:w-full" : "after:w-0 hover:after:w-full"}`;

function MobileNavigation({ open }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.nav
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          className="lg:hidden fixed inset-x-0 top-20 bottom-0 z-50 bg-ll-cream overflow-y-auto"
          data-testid="header-menu"
          aria-label="Mobiele navigatie"
        >
          <div className="px-6 pt-6 pb-16">
            <ul className="divide-y divide-ll-forest/10">
              {MAIN.map((i) => (
                <li key={i.to}>
                  <NavLink to={i.to} data-testid={`menu-link-${i.to.replace(/\W+/g, "-")}`} className="flex items-center justify-between py-4 font-heading text-2xl text-ll-forest">
                    {i.label} <ArrowRight size={18} className="text-ll-orange" />
                  </NavLink>
                </li>
              ))}
            </ul>
            <Link to="/pilot" data-testid="mobile-menu-cta-pilot" className="mt-8 flex items-center justify-center gap-2 rounded-full bg-ll-orange text-white py-4 text-base font-semibold">
              Ontdek de pilot <ArrowRight size={18} />
            </Link>
            <p className="mt-10 text-[11px] uppercase tracking-[0.28em] text-ll-leaf">Meer</p>
            <ul className="mt-3 grid grid-cols-1 gap-1">
              {MORE.map((i) => (
                <li key={i.to}>
                  <NavLink to={i.to} data-testid={`menu-link-${i.to.replace(/\W+/g, "-")}`} className="block py-2 text-ll-ink/80">
                    {i.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-[60] bg-ll-cream/90 backdrop-blur-md border-b border-ll-forest/10" data-testid="site-header">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 h-20 flex items-center justify-between gap-6">
          <Link to="/" data-testid={NAV.logo} className="shrink-0" aria-label="LevelLeven home">
            <Logo />
          </Link>
          <nav className="hidden lg:flex items-center gap-7 xl:gap-9" aria-label="Hoofdnavigatie">
            {MAIN.map((i) => (
              <NavLink key={i.to} to={i.to} className={linkCls} data-testid={`nav-${i.to.replace(/\W+/g, "-")}`}>
                {i.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/pilot"
              data-testid="header-cta-pilot"
              className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-ll-orange hover:bg-ll-forest text-white px-5 py-2.5 text-sm font-semibold transition-colors"
            >
              Ontdek de pilot <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <button
              data-testid={NAV.mobileToggle}
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-12 h-12 rounded-full text-ll-forest hover:bg-ll-forest/5"
              aria-label={open ? "Sluit menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>
      <MobileNavigation open={open} />
    </>
  );
}
